import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, NavLink, Outlet, useNavigate, useParams, useResolvedPath } from 'react-router-dom';
import classNames from 'classnames';

import {
  PermissionKeys,
  useGetCompaniesByIdQuery,
  useGetAgreementsQuery,
  useGetCompaniesByCompanyIdMembersQuery,
  useGetCompaniesByCompanyIdContactPersonAssignmentsQuery,
} from '@client/api';
import { PageBreadcrumbs, PageNavigation, Skeleton, Typography, useRouteDocumentTitle } from '@client/toolkit';

import { CompanyDeleteModal, CompanyEditModal } from './components';

import { BackButton, PageContentAnimation } from '../../../components';
import { CompaniesPaths } from '../companies.router';
import { PageListLayout } from '../../../components/layout/page-list-layout';
import { PermissionBasedActionButton } from '../../../components/permission-based-button/permission-based-button';
import { useUserPermission } from '../../../hooks/useUserPermission/useUserPermission';
import { HasUserPermission } from '../../../components/permission/has-user-permission';
import { NoPackageAccessPage } from '../../../components/no-access-modal';

export function CompanyRoute() {
  const { t, ready } = useTranslation('companies');
  const { t: tCommon } = useTranslation();

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyPath = useResolvedPath(CompaniesPaths.Company);

  useRouteDocumentTitle(t('company.documentTitle'), companyPath.pathname);

  const { data: companyData, isFetching, refetch } = useGetCompaniesByIdQuery({ id: id ?? '' }, { skip: id == null });

  const isTenant = companyData?.roles?.includes('Tenant') ?? false;

  const { hasPermission } = useUserPermission();

  const { data: agreementsData } = useGetAgreementsQuery(
    { 'companyId.Equal': id, 'pagination.Limit': 0, 'pagination.Offset': 0 },
    { skip: id == null },
  );
  const agreementsCount = agreementsData?.totalCount ?? 0;

  const { data: membersCountData, refetch: refetchMembersCount } = useGetCompaniesByCompanyIdMembersQuery(
    { companyId: id ?? '', 'pagination.Limit': 0, 'pagination.Offset': 0 },
    { skip: id == null || !hasPermission([PermissionKeys.Tenant.CompanyUsersList]) },
  );
  const usersCount = membersCountData?.totalCount ?? 0;

  const { data: contactPersonsData } = useGetCompaniesByCompanyIdContactPersonAssignmentsQuery(
    { companyId: id ?? '' },
    { skip: id == null },
  );
  const contactPersonsCount = contactPersonsData?.length ?? 0;

  const tabs = useMemo(() => {
    const items = [
      {
        key: 'company',
        label: t('detailTabs.company'),
        to: generatePath(CompaniesPaths.Company, { id }),
        end: true,
      },
      {
        key: 'agreements',
        label: `${t('detailTabs.agreements')} (${agreementsCount})`,
        to: generatePath(CompaniesPaths.CompanyAgreements, { id }),
        end: false,
      },
    ];

    if (hasPermission([PermissionKeys.Tenant.CompanyUsersList])) {
      items.push({
        key: 'users',
        label: `${t('detailTabs.users')} (${usersCount})`,
        to: generatePath(CompaniesPaths.CompanyUsers, { id }),
        end: false,
      });
    }

    items.push({
      key: 'contactPersons',
      label: `${t('detailTabs.contactPersons')} (${contactPersonsCount})`,
      to: generatePath(CompaniesPaths.CompanyContactPersons, { id }),
      end: false,
    });

    if (isTenant) {
      items.push({
        key: 'invoices',
        label: t('detailTabs.invoices'),
        to: generatePath(CompaniesPaths.CompanyInvoices, { id }),
        end: false,
      });
    }

    return items;
  }, [t, id, hasPermission, isTenant, agreementsCount, usersCount, contactPersonsCount]);

  const [editing, setEditing] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const handleBack = () => {
    navigate(generatePath(CompaniesPaths.Companies));
  };

  const handleCompanyEditClick = () => {
    setEditing(true);
  };

  const handleEditClose = () => {
    setEditing(false);
  };

  const handleEditAfterSubmit = () => {
    setEditing(false);
    refetch();
  };

  const handleClickDeleteCompany = () => {
    setDeleting(true);
  };

  const handleCloseDeleteCompany = () => {
    setDeleting(false);
  };

  const handleDeleteCompany = () => {
    navigate(generatePath(CompaniesPaths.Companies));
  };

  return (
    <HasUserPermission permissions={[PermissionKeys.Tenant.CompanyView]} fallback={<NoPackageAccessPage />}>
      <PageListLayout>
        <PageNavigation>
          <PageBreadcrumbs className="py-4">
            <PageBreadcrumbs.Link onClick={handleBack}>
              {ready ? t('overview') : <Skeleton variant="text" width="w-24" />}
            </PageBreadcrumbs.Link>
            <PageBreadcrumbs.Link>
              {ready ? t('company.title') : <Skeleton variant="text" width="w-24" />}
            </PageBreadcrumbs.Link>
            <PageBreadcrumbs.Link>
              {isFetching ? <Skeleton variant="text" width="w-32" /> : companyData?.name}
            </PageBreadcrumbs.Link>
          </PageBreadcrumbs>
        </PageNavigation>
        <PageContentAnimation>
          <div className="flex w-full justify-between mb-4">
            <BackButton />
            <div className="flex">
              <PermissionBasedActionButton
                color="danger"
                onClick={handleClickDeleteCompany}
                permissions={[PermissionKeys.Tenant.CompanyDelete]}
                title={tCommon('delete')}
              />
            </div>
          </div>
        </PageContentAnimation>

        <PageContentAnimation className="mb-4">
          <div className="flex gap-8 border-b border-border w-full">
            {tabs.map((tab) => (
              <NavLink
                key={tab.key}
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  classNames(
                    'pb-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'text-brand-primary border-b-2 border-brand-primary'
                      : 'text-white hover:text-brand-primary',
                  )
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </div>
        </PageContentAnimation>

        <PageContentAnimation className="space-y-4 px-4 pt-2 pb-8 rounded-lg shadow-lg border border-border">
          <div className="mt-3 mb-1">
            {isFetching ? (
              <Skeleton variant="text" className="w-[40%]" />
            ) : (
              <Typography variant="section">{companyData?.name}</Typography>
            )}
          </div>
          <Outlet context={{ refetchMembersCount }} />
        </PageContentAnimation>

        {id != null && (
          <>
            <CompanyEditModal
              id={id}
              open={editing}
              company={companyData}
              onClose={handleEditClose}
              onAfterSubmit={handleEditAfterSubmit}
            />
            <CompanyDeleteModal
              company={companyData ?? null}
              open={deleting}
              onCancel={handleCloseDeleteCompany}
              onDelete={handleDeleteCompany}
            />
          </>
        )}
      </PageListLayout>
    </HasUserPermission>
  );
}
